import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Box,
    Grid,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    OutlinedInput,
    InputAdornment,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { capitalizeFirstLetter, validateInputValue } from 'utils/commonHelpers';
import ApiHelper from 'utils/apiHelper';

const EditScheduledEventDialog = ({ onClose, data, index }) => {
    const [days, setDays] = useState({
        sunday: data.days['sunday'],
        monday: data.days['monday'],
        tuesday: data.days['tuesday'],
        wednesday: data.days['wednesday'],
        thursday: data.days['thursday'],
        friday: data.days['friday'],
        saturday: data.days['saturday']
    });
    const [eventType, setEventType] = useState(data.eventType);
    const [startTime, setStartTime] = useState(data.startTime);
    const [delayTime, setDelayTime] = useState(data.delayTime);
    const [prewarningTime, setPrewarningTime] = useState(data.prewarningTime);
    const [message, setMessage] = useState(data.message);
    const [scriptId, setScriptId] = useState(data.script);
    const [scriptsData, setScriptsData] = useState([]);
    const [eventOptions, setEventOption] = useState([]);

    useEffect(() => {
        getScriptsData();
        getSchemeType();
    }, []);

    const getSchemeType = () => {
        ApiHelper.get('/api/management/scheme_type/').then((res) => {
            const data = res.data.filter((schemeType) => schemeType.schemetypeID >= 2);
            setEventOption(data);
        });
    };

    const getScriptsData = () => {
        ApiHelper.get('/api/server/scripts/')
            .then((res) => {
                setScriptsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const handleChangeDays = (e) => {
        setDays({
            ...days,
            [e.target.name]: e.target.checked
        });
    };

    const handleSubmit = () => {
        if (
            days.sunday === false &&
            days.monday === false &&
            days.tuesday === false &&
            days.wednesday === false &&
            days.thursday === false &&
            days.friday === false &&
            days.saturday === false
        ) {
            toast.error('Please select the day');
            return true;
        }
        if (startTime === '') {
            toast.error('Please select the start time');
            return true;
        }
        if (prewarningTime === '') {
            toast.error('Please fill the pre-warning time');
            return true;
        }
        if (validateInputValue(prewarningTime, 'minutes') === false) {
            toast.error('Pre-warning time should be less than 60');
            return true;
        }
        if (validateInputValue(delayTime, 'hours') === false) {
            toast.error('Delay time should be less than 24');
            return true;
        }
        if (eventType === 'script' && scriptId === '') {
            toast.error('Please select the script');
            return true;
        }
        const updatedScheduledEventData = {
            id: data.id,
            days,
            eventType,
            startTime,
            delayTime,
            prewarningTime,
            message,
            script: eventType === 'script' ? scriptId : ''
        };
        const scheduledEventsData = JSON.parse(localStorage.getItem('policyScheduledEventsData'));
        scheduledEventsData[index] = updatedScheduledEventData;
        localStorage.setItem('policyScheduledEventsData', JSON.stringify(scheduledEventsData));
        onClose();
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit Scheduled Event</DialogTitle>
            <DialogContent sx={{ maxWidth: '600px' }}>
                <Box sx={{ mt: 2 }}>
                    <FormGroup aria-label="position" row>
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="sunday" checked={days.sunday} onChange={handleChangeDays} />}
                            label="Sun"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="monday" checked={days.monday} onChange={handleChangeDays} />}
                            label="Mon"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="tuesday" checked={days.tuesday} onChange={handleChangeDays} />}
                            label="Tue"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="wednesday" checked={days.wednesday} onChange={handleChangeDays} />}
                            label="Wed"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="thursday" checked={days.thursday} onChange={handleChangeDays} />}
                            label="Thr"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="friday" checked={days.friday} onChange={handleChangeDays} />}
                            label="Fri"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="saturday" checked={days.saturday} onChange={handleChangeDays} />}
                            label="Sat"
                            labelPlacement="top"
                        />
                    </FormGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Event Selector</InputLabel>
                                <Select label="Event Selector" value={eventType} onChange={(e) => setEventType(e.target.value)}>
                                    {eventOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.name}>
                                            {capitalizeFirstLetter(data.name)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>Start Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="prewarningTime">Pre-warning Time</InputLabel>
                                <OutlinedInput
                                    id="prewarningTime"
                                    type="number"
                                    label="Pre-warning Time"
                                    value={prewarningTime}
                                    onChange={(e) => setPrewarningTime(e.target.value)}
                                    endAdornment={<InputAdornment position="end">min(s)</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="delayTime">Delay Time</InputLabel>
                                <OutlinedInput
                                    id="delayTime"
                                    type="number"
                                    label="Delay Time"
                                    value={delayTime}
                                    onChange={(e) => setDelayTime(e.target.value)}
                                    endAdornment={<InputAdornment position="end">hour(s)</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                sx={{ width: '100%', mt: 1 }}
                                label={'Message'}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                        </Grid>
                        {eventType === 'script' && (
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select script</InputLabel>
                                    <Select label="Select script" value={scriptId} onChange={(e) => setScriptId(e.target.value)}>
                                        {scriptsData.map((data, key) => (
                                            <MenuItem key={key} value={data.id}>
                                                {data.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

EditScheduledEventDialog.propTypes = {
    onClose: PropTypes.func,
    data: PropTypes.object,
    index: PropTypes.number
};

export default EditScheduledEventDialog;
